import * as ngCore from '@angular/core'
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop'
import * as rxjs from 'rxjs'

import * as text from '../text'

import * as models from './models'

/**
 * Component for searching data
 *
 *
 * #### Inputs
 * - {@linkcode AtnDsSearchComponent.config config}!
 * - {@linkcode AtnDsSearchComponent.value value}?
 * - {@linkcode AtnDsSearchComponent.disabled disabled}?
 *
 * #### Outputs
 * - {@linkcode AtnDsSearchComponent.valueChange searchChange}
 *
 * @example
 * ```html
 * <!-- Simple example -->
 * <atn-ds-search
 *  [value]="searchTerm"
 *  (valueChange)="searchChangeHandler($event)"
 *  [config]="config"
 * />
 * ```
 */
@ngCore.Component({
  imports: [text.Component],
  selector: 'atn-ds-search',
  standalone: true,
  styleUrls: ['./search.component.scss'],
  templateUrl: './search.component.html',
})
export class AtnDsSearchComponent {
  /**
   * Search configuration that determines place holder text and debounce time when searching
   */
  public readonly configSg = ngCore.input<models.Config | undefined>(
    undefined,
    { alias: 'config' },
  )

  /** TODO:  */
  public readonly placeholderSg = ngCore.computed(
    (config = this.configSg()) => config?.placeholder ?? 'Search...',
  )

  /**
   * Initial value passed for search e.g. previous search taken from the state
   */
  public readonly valueSg = ngCore.input<string | undefined>(undefined, {
    alias: 'value',
  })

  /**
   * Determines if the search is disabled
   */
  public readonly disabledSg = ngCore.input<boolean | undefined>(false, {
    alias: 'disabled',
  })

  /**
   * Event emitter that emits new search text to outer component which will then
   * deal with the changes in search
   */
  public readonly valueChange = outputFromObservable(
    toObservable(this.configSg).pipe(
      rxjs.switchMap(config =>
        this._valueChange$.pipe(rxjs.debounceTime(config?.debounceTime ?? 0)),
      ),
    ),
  )

  /**
   * Stream of all values emitted by the component, serves as a source for {@link valueChange},
   * so we can additionally attach debouch logic to it
   */
  public readonly _valueChange$ = new rxjs.Subject<string | undefined>()

  /**
   * Emits a new  value to the {@link _valueChange$}
   */
  public onValueChangeHandler(value: string | undefined) {
    this._valueChange$.next(value)
  }
}
